import { useEffect, useRef } from 'react';
import { ATLAS_COLS, GLYPHS, buildAtlas, ensureGlyphFont, sampleWord, type Point2 } from './glyphs';

const COUNT = 700;
const CELL = 96;

const FIELD_MS = 4200;
const MORPH_IN_MS = 1300;
const HOLD_MS = 2200;
const MORPH_OUT_MS = 1100;
const CYCLE_MS = FIELD_MS + MORPH_IN_MS + HOLD_MS + MORPH_OUT_MS;

interface GlyphField2DProps {
  readonly words: readonly string[];
  readonly displayFont: string;
  /** Draw exactly one resting frame and stop — the reduced-motion path. */
  readonly still: boolean;
  readonly active: boolean;
}

interface Particle {
  hx: number;
  hy: number;
  tx: number;
  ty: number;
  cell: number;
  seed: number;
}

const smooth = (t: number) => t * t * (3 - 2 * t);

/** Must track `--color-muted` / `--color-acid` in index.css (v9 LOKTA values). */
const FIELD_COLOR = '#857c69';
const WORD_COLOR = '#be3b0b';

/**
 * Canvas 2D has no equivalent of the shader's colour mix — `drawImage` paints
 * the atlas exactly as rasterised, which is white. Pre-tinting one atlas per
 * colour and cross-fading between them is far cheaper than re-tinting per
 * glyph per frame, and keeps the fallback the same colour as the WebGL path
 * instead of a white word nobody designed.
 */
function tintAtlas(source: HTMLCanvasElement, color: string): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = source.width;
  out.height = source.height;
  const ctx = out.getContext('2d');
  if (!ctx) return source;
  ctx.drawImage(source, 0, 0);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, out.width, out.height);
  return out;
}

/**
 * Canvas-2D glyph field. One implementation covering all three non-WebGL
 * cases (CLAUDE.md §11): small screens, machines without WebGL, and
 * `prefers-reduced-motion`, which draws a single composed frame and stops.
 *
 * Deliberately the *same* effect at lower fidelity rather than a different
 * one — it shares the atlas and word sampler with the WebGL renderer, so the
 * fallback cannot drift away from what it is standing in for.
 */
export function GlyphField2D({ words, displayFont, still, active }: GlyphField2DProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    let cancelled = false;
    let fieldAtlas: HTMLCanvasElement | null = null;
    let wordAtlas: HTMLCanvasElement | null = null;
    const particles: Particle[] = [];
    let dpr = 1;
    let w = 0;
    let h = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = rect.width;
      h = rect.height;
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      for (const p of particles) {
        p.hx = Math.random() * w;
        p.hy = Math.random() * h;
      }
    };

    const loadWord = (index: number) => {
      const word = words[index % words.length];
      if (!word) return;
      const pts: Point2[] = sampleWord(word, COUNT, displayFont);
      if (pts.length === 0) return;
      const ww = Math.min(w * 0.8, 620);
      // On narrow screens the headline occupies the middle band, so the word
      // resolves into the empty space above it instead of straight through it.
      // (The WebGL path solves the same collision by moving sideways, which
      // only works when there is a side to move to.)
      const cy = w < 640 ? h * 0.22 : h / 2;
      particles.forEach((p, i) => {
        const s = pts[i]!;
        p.tx = w / 2 + s.x * ww;
        p.ty = cy - s.y * (ww / 4);
      });
    };

    const draw = (morph: number, time: number) => {
      if (!fieldAtlas || !wordAtlas) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      for (const p of particles) {
        const m = smooth(Math.min(1, Math.max(0, (morph - p.seed * 0.25) / 0.75)));
        const t = time * 0.00016 + p.seed * 6.2831853;
        const hx = p.hx + Math.sin(t) * 14;
        const hy = p.hy + Math.cos(t * 0.78) * 11;
        const x = hx + (p.tx - hx) * m;
        const y = hy + (p.ty - hy) * m;
        // Shrink while assembling, matching the WebGL path — otherwise the
        // letterforms fill in solid and stop reading as built from glyphs.
        const size = 18 * (1 - m * 0.22);

        const col = p.cell % ATLAS_COLS;
        const row = Math.floor(p.cell / ATLAS_COLS);
        const fieldAlpha = 0.34 * (1 - m);
        const wordAlpha = 0.82 * m;

        // Both layers only overlap during the ~1.3s transition; at rest one
        // of them is fully transparent and is skipped entirely.
        if (fieldAlpha > 0.02) {
          ctx.globalAlpha = fieldAlpha;
          ctx.drawImage(fieldAtlas, col * CELL, row * CELL, CELL, CELL, x, y, size, size);
        }
        if (wordAlpha > 0.02) {
          ctx.globalAlpha = wordAlpha;
          ctx.drawImage(wordAtlas, col * CELL, row * CELL, CELL, CELL, x, y, size, size);
        }
      }
      ctx.globalAlpha = 1;
    };

    void ensureGlyphFont().then(() => {
      if (cancelled) return;
      const raw = buildAtlas();
      fieldAtlas = tintAtlas(raw, FIELD_COLOR);
      wordAtlas = tintAtlas(raw, WORD_COLOR);

      for (let i = 0; i < COUNT; i++) {
        particles.push({
          hx: 0,
          hy: 0,
          tx: 0,
          ty: 0,
          cell: Math.floor(Math.random() * GLYPHS.length),
          seed: Math.random(),
        });
      }
      resize();
      window.addEventListener('resize', resize);

      if (still) {
        // One frame, no loop. The resting field is the composition.
        draw(0, 0);
        return;
      }

      let wordIndex = -1;
      const start = performance.now();
      const tick = (now: number) => {
        const elapsed = now - start;
        const cycle = Math.floor(elapsed / CYCLE_MS);
        if (cycle !== wordIndex) {
          wordIndex = cycle;
          loadWord(cycle);
        }
        const phase = elapsed % CYCLE_MS;
        let morph = 0;
        if (phase > FIELD_MS && phase <= FIELD_MS + MORPH_IN_MS) {
          morph = (phase - FIELD_MS) / MORPH_IN_MS;
        } else if (phase <= FIELD_MS + MORPH_IN_MS + HOLD_MS && phase > FIELD_MS + MORPH_IN_MS) {
          morph = 1;
        } else if (phase > FIELD_MS + MORPH_IN_MS + HOLD_MS) {
          morph = 1 - (phase - FIELD_MS - MORPH_IN_MS - HOLD_MS) / MORPH_OUT_MS;
        }
        draw(morph, elapsed);
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
    // `active` is intentionally excluded: the loop is torn down and rebuilt by
    // the parent unmounting this component, not by restarting mid-effect.
  }, [words, displayFont, still, active]);

  return <canvas ref={canvasRef} aria-hidden="true" className="h-full w-full" />;
}
